import { UserInformation } from '../keycloak/user-information.model';
import { DateUtil } from '../../util/date.util';
import { OrganizationStatus } from './organization-status.enum';
import { MediaImage } from '../media/media-image.model';
import { VideoLink } from '../media/video-link.model';
import { AudioLink } from '../media/audio-link.model';
import { Tag } from '../metadata/tag.model';
import { OrganizationReach } from './organization-reach.model';
import { OrganizationImpactType } from './organization-impact-type.model';
import { OrganizationProvince } from './organization-province.model';
import { OrganizationHeritageType } from './organization-heritage-type.model';
import { OrganizationGoal } from './organization-goal.model';
import { SocialMedia } from '../metadata/social-media.model';
import { ApplicantDetails } from '../ownership-claim/applicant-details.model';

export class Organization {
  // Organization
  public id: string;
  public slug: string;
  public isActive: boolean;
  public createdAt: Date;
  public publishedAt: Date;
  public user: UserInformation;
  public name: string;
  public website: string;
  public email: string;
  public phoneNumber: string;
  public owner: ApplicantDetails;
  // Contact person
  public contactName: string;
  public contactFunction: string;
  public contactEmail: string;
  public shortDescription: string;
  public status: OrganizationStatus;
  public content: string; // HTML containing the full description of this organization
  // Location
  public location: string;
  public latitude: string;
  public longitude: string;
  // Related information
  public reach: OrganizationReach;
  public impactTypes: OrganizationImpactType[];
  public provinces: OrganizationProvince[] = [];
  public heritageTypes: OrganizationHeritageType[] = [];
  public goalList: OrganizationGoal[] = [];
  // Images
  public headerImage: MediaImage;
  public logoImage: MediaImage;
  public carouselImages: MediaImage[];
  // Media
  public videoLinks: VideoLink[];
  public audioLinks: AudioLink[];
  // Metadata
  public tagList: Tag[] = [];
  public socialMediaList: SocialMedia[] = [];
  public relatedOrganizationList: Organization[] = [];
  public comments: string;
  // FE useful properties
  public hasContactPersonData: boolean;
  public hasSocialMediaData: boolean;
  public hasOwner: boolean;

  constructor(data: any = {}) {
    this.id = data?.id ?? '';
    this.slug = data?.slug ?? '';
    this.isActive = data?.isActive ?? false;
    this.createdAt = data.createdAt;
    this.publishedAt = DateUtil.createOrInitialise(data?.publishedAt);
    this.name = data?.name ?? '';
    this.website = data?.website ?? '';
    this.email = data?.email ?? '';
    this.phoneNumber = data?.phoneNumber ?? '';
    this.contactName = data?.contactName ?? '';
    this.contactFunction = data?.contactFunction ?? '';
    this.contactEmail = data?.contactEmail ?? '';
    this.shortDescription = data?.shortDescription ?? '';
    this.status = data?.status ?? 0;
    this.user = data?.user ?? UserInformation.createFromApi(data?.user);
    this.content = data?.content ?? '';
    this.owner = ApplicantDetails.createFromApi(data?.owner);
    // Location
    this.location = data?.location ?? '';
    this.latitude = data?.latitude ?? '0';
    this.longitude = data?.longitude ?? '0';
    // Related information
    this.reach = data?.reach ?? data?.organizationReach;
    this.impactTypes = data?.impactTypes ?? [];
    this.provinces = data?.provinces ?? [];
    this.heritageTypes = data?.heritageTypes ?? [];
    this.goalList = data?.goalList ?? [];
    // Images
    this.headerImage = data?.headerImage ?? null;
    this.logoImage = data?.logoImage ?? null;
    this.carouselImages = data?.carouselImages ?? [];
    // Media
    this.videoLinks = data?.videoLinks ?? [];
    this.audioLinks = data?.audioLinks ?? [];
    // Metadata
    this.tagList = data?.tagList ?? [];
    // Social media
    this.socialMediaList = data?.socialMediaList ?? [];
    this.relatedOrganizationList = data?.relatedOrganizationList ?? [];
    this.comments = data?.comments ?? '';
    // FE useful properties
    this.hasContactPersonData = this.contactName.length > 0 || this.contactFunction.length > 0 || this.contactEmail.length > 0;
    this.hasSocialMediaData = this.socialMediaList.length > 0;
    this.hasOwner = data?.hasOwner ?? false;
  }

  public static createFromApi(data: any = {}): Organization {
    const user: UserInformation = UserInformation.createFromApi(data?.user);
    const reach: OrganizationReach = OrganizationReach.createFromApi(data?.scope ?? data?.organizationReach);
    const impactTypes: OrganizationImpactType[] = (data?.impactTypes ?? []).map((impactTypeData: any) => OrganizationImpactType.createFromApi(impactTypeData));
    const provinces: OrganizationProvince[] = (data?.provinces ?? []).map((provinceData: any) => OrganizationProvince.createFromApi(provinceData));
    const heritageTypes: OrganizationHeritageType[] = (data?.heritageTypes ?? []).map((heritageData: any) => OrganizationHeritageType.createFromApi(heritageData));
    const goalList: OrganizationGoal[] = (data?.heritageGoals ?? data?.goalList ?? []).map((goalData: any) => OrganizationGoal.createFromApi(goalData));
    const headerImage = MediaImage.createFromApi(data.headerImage);
    const logoImage = MediaImage.createFromApi(data.logoImage);
    const carouselImages: MediaImage[] = (data?.carouselImages ?? [])
      .map((imageData: any, index: number) => MediaImage.createFromApi({...imageData, index}))
      // Sort
      .sort((imageA: MediaImage, imageB: MediaImage): boolean => imageA.metadata.order < imageB.metadata.order)
      // Correct any order mismatch from deletions or adding with wrong order
      .map((mediaImage: MediaImage, index: number): MediaImage => {
        mediaImage.metadata.order = index;
        return mediaImage;
      });
    const videoLinks: VideoLink[] = (data?.videoLinks ?? []).map((videoData: any) => VideoLink.createFromApi(videoData));
    const audioLinks: AudioLink[] = (data?.media ?? []).map((audioData: any) => AudioLink.createFromApi(audioData));
    const tagList = (data?.tags ?? data?.tagList ?? []).map((tagData: any) => Tag.createFromApi(tagData));
    const socialMediaList = (data?.socialMedia ?? data?.socialMediaList ?? []).map((socialMedia: any) => SocialMedia.createFromApi(socialMedia));
    const relatedOrganizationList = (data?.relatedOrganizations ?? data?.relatedOrganizationList ?? []).map((relatedOrganizationData: any) => Organization.createFromApi(relatedOrganizationData));
    const owner: ApplicantDetails = ApplicantDetails.createFromApi(data?.owner);

    return new Organization({
      id: data?.id ?? '',
      // Organization
      slug: data?.slug ?? '',
      collectionNumber: data?.collectionId ?? data?.collectionNumber ?? '',
      isActive: data?.isActive ?? false,
      createdAt: DateUtil.createOrInitialise(data?.createdAt),
      publishedAt: DateUtil.createOrInitialise(data?.publishedAt),
      name: data?.name ?? '',
      website: data?.website ?? '',
      email: data?.email ?? '',
      phoneNumber: data?.phoneNumber ?? '',
      contactName: data?.contactName ?? '',
      contactFunction: data?.contactFunction ?? '',
      contactEmail: data?.contactEmail ?? '',
      shortDescription: data?.shortDescription ?? '',
      status: data?.status ?? 0,
      user,
      content: data?.content ?? '',
      // Location
      location: data?.location ?? '',
      latitude: data?.lat ?? data?.latitude ?? '0',
      longitude: data?.lng ?? data?.longitude ?? '0',
      owner,
      // Related information
      reach,
      impactTypes,
      provinces,
      heritageTypes,
      goalList,
      // Images
      headerImage,
      logoImage,
      carouselImages,
      // Multimedia
      videoLinks,
      audioLinks,
      // Metadata
      tagList,
      // Social media
      socialMediaList,
      relatedOrganizationList,
      comments: data?.internalComment ?? data?.comments ?? '',
      // FE useful properties
      hasOwner: data?.owner !== null,
    });
  }

  public processForApi(publish: boolean): Record<string, any> {
    const result: Record<string, any> = {
      "name": this.name,
      "website": this.website,
      "email": this.email,
      "phoneNumber": this.phoneNumber,
      "contactName": this.contactName,
      "contactFunction": this.contactFunction,
      "contactEmail": this.contactEmail,
      "shortDescription": this.shortDescription,
      "content": this.content,
      "location": this.location,
      "lat": this.latitude,
      "lng": this.longitude,
      "internalComment": this.comments,
      "isActive": this.isActive,
      "publish": publish,
      "owner": this.owner.processForApi(),
      "scope": this.reach && this.reach.id ? this.reach?.processForApi() : null,
      "impactTypes": this.impactTypes.map(impactType => impactType.processForApi()),
      "provinces": this.provinces.map(province => province.processForApi()),
      "heritageTypes": this.heritageTypes.map(heritageType => heritageType.processForApi()),
      "heritageGoals": this.goalList.map(goal => goal.processForApi()),
      "carouselImages": this.carouselImages.map(image => image.processForApi()),
      "videoLinks": this.videoLinks.map(video => video.processForApi()),
      "media": this.audioLinks.map(audio => audio.processForApi()),
      "tags": this.tagList,
      "socialMedia": this.socialMediaList.map(socialMedia => socialMedia.processForApi()),
      "relatedOrganizations": this.relatedOrganizationList.map((organization: Organization): string => organization.id),
    };
    // Header image
    if (this?.headerImage?.id?.length ?? 0 > 0) {
      result['headerImage'] = this.headerImage.processForApi();
    }
    // Header image
    if (this?.logoImage?.id?.length ?? 0 > 0) {
      result['logoImage'] = this.logoImage.processForApi();
    }
    return result;
  }
}
